/**
 * RowActions — per-row action buttons.
 *
 * Visual/UX parity with MasterCrudTable's RowActionsCell:
 * - ≤3 visible actions → all rendered inline (direct).
 * - 4+ visible actions → 2 direct actions (priority: edit slot, then the
 *   "company-enable" special action) + the remaining actions collapsed into
 *   a "more actions" kebab (portal, fixed position).
 *
 * Exception to the NEVER-HIDE permission invariant (see engine/permissionEngine.ts):
 * the "Editar" slot does NOT follow never-hide — it matches MasterCrud's actual
 * behavior exactly (MasterCrud.tsx: `if (canRead && canUpdate) defaultActions.push({id:'edit',...})`):
 * when the user lacks UPDATE permission the slot is simply omitted, with no toast.
 * Unlike MasterCrud (which has no per-row read-only fallback at all — canRead only
 * gates the whole list), the MPV replaces the omitted "Editar" with "Visualizar"
 * whenever canRead is true and canUpdate is false, so a read-only user always has
 * a way to open the row instead of being left with zero actions. Consumers can
 * still force the "view" slot on/off explicitly via `defaultActions.view.show`,
 * which always wins over this fallback. "Cambiar estado" and custom actions keep
 * the never-hide + warning-toast behavior unchanged.
 *
 * @module components/list/RowActions
 * @see FMPV-MVWC-E002-S003 I-005
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Pencil } from 'lucide-react';
import {
  EyeIcon,
  MinusCircleIcon,
  CheckCircleIcon,
  EllipsisVerticalIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import type { MasterPatternViewPermissions } from '../../types/permissions.types';
import type { MasterPatternViewEntity } from '../../types/service.types';
import type { MasterPatternViewCustomAction, MasterPatternViewDefaultActions } from '../../types/props.types';
import { isActionAllowed, getDisabledToastMessage } from '../../engine/permissionEngine';
import { useToast, type ToastCallback } from '../../hooks/useToast';

export interface RowActionsProps<T extends MasterPatternViewEntity = MasterPatternViewEntity> {
  row: T;
  permissions?: MasterPatternViewPermissions;
  onOpenView: (row: T) => void;
  onOpenEdit: (row: T) => void;
  onChangeStatus?: (row: T) => void;
  actions?: MasterPatternViewCustomAction<T>[];
  rowActions?: (row: T) => React.ReactNode;
  toast?: ToastCallback;
  showInternalToasts?: boolean;
  defaultActions?: MasterPatternViewDefaultActions<T>;
  /**
   * 'table' (default): MasterCrudTable's rule — ≤3 direct, 4+ collapses to 2 direct + kebab.
   * 'card': MasterCRUDCard's rule — only "company-enable" (MasterCrud's 'link') stays direct;
   * everything else always collapses into ONE hamburger (Bars3Icon), regardless of count.
   */
  variant?: 'table' | 'card';
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

interface ResolvedRowAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  testId: string;
  /** 'danger' (red, deactivating) / 'success' (green, activating) — only changeStatus uses this. */
  tone?: 'danger' | 'success';
}

function resolveActionConfig<T>(
  value: boolean | ((row: T) => boolean) | undefined,
  row: T,
  defaultValue: boolean,
): boolean {
  if (value === undefined) return defaultValue;
  if (typeof value === 'function') return value(row);
  return value;
}

// Priority ids for the "2 direct actions" slot when 4+ actions collapse — mirrors
// MasterCrudTable's ['edit', 'link'] priority (company-enable is MPV's analog to 'link').
const PRIORITY_IDS = ['edit', 'company-enable'];
const MAX_DIRECT = 3;

// Small elastic "pop" on hover/press — shared by every row-action button (direct
// icons AND the kebab trigger), so none of them feels like the odd one out.
const bounceTransition = { type: 'spring' as const, bounce: 0.55, duration: 0.4 };

export function RowActions<T extends MasterPatternViewEntity = MasterPatternViewEntity>({
  row,
  permissions,
  onOpenView,
  onOpenEdit,
  onChangeStatus,
  actions = [],
  rowActions,
  toast: toastProp,
  showInternalToasts = true,
  defaultActions,
  variant = 'table',
  t,
}: RowActionsProps<T>) {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  const { showWarning } = useToast(toastProp, showInternalToasts);
  const canRead = isActionAllowed(permissions, 'READ', 'UNIVERSAL');
  const canUpdate = isActionAllowed(permissions, 'UPDATE', 'UNIVERSAL');
  const canChangeStatus = isActionAllowed(permissions, 'CHANGE_STATUS', 'UNIVERSAL');

  // Defaults to appearing only as the read-only fallback (canRead && !canUpdate) —
  // MasterCrud has no "Visualizar" action at all, but leaving the row with zero
  // actions when the user can't edit is worse than showing one. Explicit
  // defaultActions.view.show (true or false) always overrides this default.
  const showView = resolveActionConfig(defaultActions?.view?.show, row, canRead && !canUpdate);
  const disabledView = resolveActionConfig(defaultActions?.view?.disabled, row, false);
  const showEdit = resolveActionConfig(defaultActions?.edit?.show, row, true);
  const disabledEdit = resolveActionConfig(defaultActions?.edit?.disabled, row, false);
  const showChangeStatus = resolveActionConfig(defaultActions?.changeStatus?.show, row, true);
  const disabledChangeStatus = resolveActionConfig(defaultActions?.changeStatus?.disabled, row, false);

  const handleView = useCallback(() => {
    if (!canRead) {
      showWarning(getDisabledToastMessage('READ'));
      return;
    }
    onOpenView(row);
  }, [canRead, showWarning, onOpenView, row]);

  // "Editar"/"Detalle" share this slot — permission decides which one renders,
  // so no permission check/toast is needed on click for either.
  const handleEdit = useCallback(() => {
    onOpenEdit(row);
  }, [onOpenEdit, row]);

  const handleChangeStatus = useCallback(() => {
    if (!canChangeStatus) {
      showWarning(getDisabledToastMessage('CHANGE_STATUS'));
      return;
    }
    onChangeStatus?.(row);
  }, [canChangeStatus, showWarning, onChangeStatus, row]);

  const resolvedActions: ResolvedRowAction[] = [];

  if (showView) {
    resolvedActions.push({
      id: 'view',
      label: tt('viewAction', 'Visualizar'),
      icon: <EyeIcon className="w-[15px] h-[15px]" />,
      onClick: handleView,
      disabled: disabledView,
      testId: 'row-action-view',
    });
  }

  // Matches MasterCrud exactly: no UPDATE permission → the slot is omitted, period.
  if (showEdit && canUpdate) {
    resolvedActions.push({
      id: 'edit',
      label: tt('edit', 'Editar'),
      icon: <Pencil size={15} />,
      onClick: handleEdit,
      disabled: disabledEdit,
      testId: 'row-action-edit',
    });
  }

  actions
    .filter((a) => !a.position || a.position === 'row')
    .forEach((action) => {
      resolvedActions.push({
        id: action.key,
        label: action.label,
        icon: action.icon,
        onClick: () => action.onExecute(row),
        disabled: action.isDisabled?.(row) ?? false,
        testId: `row-action-${action.key}`,
      });
    });

  // Last in order, always — matches MasterCrud's activate/deactivate action (order: 200,
  // after edit/link/audit). Red "Desactivar" while active, green "Activar" while inactive.
  if (showChangeStatus) {
    resolvedActions.push({
      id: 'changeStatus',
      label: row.isActive ? tt('disableRecordAction', 'Desactivar registro') : tt('enableRecordAction', 'Activar registro'),
      icon: row.isActive
        ? <MinusCircleIcon className="w-[15px] h-[15px]" />
        : <CheckCircleIcon className="w-[15px] h-[15px]" />,
      onClick: handleChangeStatus,
      disabled: disabledChangeStatus,
      testId: 'row-action-change-status',
      tone: row.isActive ? 'danger' : 'success',
    });
  }

  let directActions: ResolvedRowAction[];
  let menuActions: ResolvedRowAction[];
  if (variant === 'card') {
    // MasterCRUDCard's rule: only "company-enable" (MasterCrud's 'link') stays direct;
    // everything else always collapses into ONE hamburger, regardless of count.
    directActions = resolvedActions.filter((a) => a.id === 'company-enable');
    menuActions = resolvedActions.filter((a) => a.id !== 'company-enable');
  } else if (resolvedActions.length <= MAX_DIRECT) {
    // MasterCrudTable's rule: ≤3 → todas directas.
    directActions = resolvedActions;
    menuActions = [];
  } else {
    // 4+ → prioriza Editar + Habilitar compañía como directas, resto al kebab.
    const priority = resolvedActions.filter((a) => PRIORITY_IDS.includes(a.id)).slice(0, 2);
    const fill =
      priority.length < 2
        ? resolvedActions.filter((a) => !priority.includes(a)).slice(0, 2 - priority.length)
        : [];
    directActions = [...priority, ...fill];
    menuActions = resolvedActions.filter((a) => !directActions.includes(a));
  }

  // Hover matches Button's own `type="outline"` exactly (same one "Cancelar" on the detail
  // page uses) — bg-{color}-100/950 + brightness filter + transition-all, not the flat
  // hardcoded-hex background these had before.
  const actionBtnClass =
    'inline-flex items-center justify-center w-7 h-7 bg-white dark:bg-zinc-900 border border-[#93d1fd] dark:border-blue-800 text-blue-600 dark:text-blue-400 rounded-md shadow-[0_1px_2px_rgba(0,0,0,0.04)] cursor-pointer transition-all duration-150 hover:bg-primary-100 dark:hover:bg-primary-950 hover:brightness-98 dark:hover:brightness-102 disabled:opacity-50 disabled:cursor-not-allowed';
  const actionBtnClassDanger =
    'inline-flex items-center justify-center w-7 h-7 bg-white dark:bg-zinc-900 border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded-md shadow-[0_1px_2px_rgba(0,0,0,0.04)] cursor-pointer transition-all duration-150 hover:bg-red-100 dark:hover:bg-red-950 hover:brightness-98 dark:hover:brightness-102 disabled:opacity-50 disabled:cursor-not-allowed';
  const actionBtnClassSuccess =
    'inline-flex items-center justify-center w-7 h-7 bg-white dark:bg-zinc-900 border border-emerald-300 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400 rounded-md shadow-[0_1px_2px_rgba(0,0,0,0.04)] cursor-pointer transition-all duration-150 hover:bg-emerald-100 dark:hover:bg-emerald-950 hover:brightness-98 dark:hover:brightness-102 disabled:opacity-50 disabled:cursor-not-allowed';

  const toneClass = (action: ResolvedRowAction) =>
    action.tone === 'danger' ? actionBtnClassDanger : action.tone === 'success' ? actionBtnClassSuccess : actionBtnClass;

  return (
    <div className="flex items-center gap-1" data-testid={`row-actions-${row.id}`}>
      {directActions.map((action) => (
        <motion.button
          key={action.id}
          type="button"
          onClick={(e) => {
            // Prevent bubbling to the table row's onClick (opens the view modal) —
            // the row is clickable, but action buttons must not trigger it too.
            e.stopPropagation();
            if (!action.disabled) action.onClick();
          }}
          disabled={action.disabled}
          whileHover={action.disabled ? undefined : { scale: 1.15 }}
          whileTap={action.disabled ? undefined : { scale: 0.9 }}
          transition={bounceTransition}
          data-testid={action.testId}
          aria-label={action.label}
          title={action.label}
          className={toneClass(action)}
        >
          {/* MasterCRUDCard.tsx just renders the 'link' action's own configured icon
              (MasterCrudCard.tsx:189, `leftIcon={linkAction.icon}`) — no chain-icon
              override. The card variant renders the action's icon as-is, same as table:
              for 'company-enable' that's Building2 (see injectedRowActions). */}
          {action.icon}
        </motion.button>
      ))}

      {menuActions.length > 0 && (
        variant === 'card' ? (
          <RowActionsMenu
            actions={menuActions}
            className="inline-flex items-center justify-center p-2 rounded-lg text-content-tertiary dark:text-dark-content-tertiary hover:text-content-primary dark:hover:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-all duration-150"
            triggerIcon={<Bars3Icon className="w-5 h-5" />}
            triggerLabel={tt('moreOptions', 'Más opciones')}
            t={tt}
          />
        ) : (
          <RowActionsMenu actions={menuActions} className={actionBtnClass} t={tt} />
        )
      )}

      {/* rowActions escape hatch — does not participate in the collapse rule */}
      {rowActions?.(row)}
    </div>
  );
}

interface RowActionsMenuProps {
  actions: ResolvedRowAction[];
  className: string;
  /** Defaults to EllipsisVerticalIcon (table kebab) — pass Bars3Icon for the card variant. */
  triggerIcon?: React.ReactNode;
  /** Defaults to "Más acciones" (table) — pass "Más opciones" for the card variant. */
  triggerLabel?: string;
  t: (key: string, defaultValue?: string) => string;
}

/**
 * Per-row "more actions" trigger — portal to document.body with fixed positioning,
 * mirroring MasterCrudTable.tsx's RowActionsCell / MasterCRUDCard.tsx's Dropdown so
 * the menu isn't clipped by the table's sticky column / overflow-auto container.
 */
function RowActionsMenu({ actions, className, triggerIcon, triggerLabel, t }: RowActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 });

  const reposition = useCallback(() => {
    const btn = btnRef.current;
    const menu = menuRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const mw = menu?.offsetWidth || 210;
    const mh = menu?.offsetHeight || 0;
    let top = r.bottom + 4;
    if (mh && top + mh > window.innerHeight - 8) top = r.top - mh - 4;
    const left = Math.max(8, r.right - mw);
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  return (
    <>
      <motion.button
        ref={btnRef}
        type="button"
        title={triggerLabel ?? t('moreActions', 'Más acciones')}
        aria-label={triggerLabel ?? t('moreActions', 'Más acciones')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.9 }}
        transition={bounceTransition}
        data-testid="row-actions-more-button"
        className={className}
      >
        {triggerIcon ?? <EllipsisVerticalIcon className="w-[15px] h-[15px]" />}
      </motion.button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            data-testid="row-actions-more-menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            className="z-[60] min-w-[210px] bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md shadow-md py-1"
          >
            {actions.map((action, idx) => (
              <React.Fragment key={action.id}>
                {/* Single separator at the midpoint — not one per item. */}
                {idx > 0 && idx === Math.floor(actions.length / 2) && (
                  <hr className="my-1 border-zinc-100 dark:border-zinc-800" />
                )}
                <button
                  type="button"
                  role="menuitem"
                  disabled={action.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                    setOpen(false);
                  }}
                  data-testid={action.testId}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-50 dark:hover:bg-zinc-800 ${
                    action.tone === 'danger'
                      ? 'text-red-600 dark:text-red-400'
                      : action.tone === 'success'
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-zinc-700 dark:text-zinc-300'
                  }`}
                >
                  <span className="w-4 h-4 flex-shrink-0 inline-flex items-center justify-center">{action.icon}</span>
                  <span>{action.label}</span>
                </button>
              </React.Fragment>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
